import { UUID } from "angular2-uuid";

/**
 * Use this class for storing application data instead of direct localStorage or sessionStorage.
 * */
export class AppStorage {
    // Rename this value each time you fork the project to avoid local stored parameters intersection!
    static readonly ApplicationPrefix = "mipro-medical";

    // Use constants for stored objects keys naming to avoid inconsistency.
    static readonly SimulatedHttpStatusCodeKey = "simulated-http-status-code";
    static readonly RealUserKey = "real-user";
    static readonly LogoutReasonMessageKey = "logout-reason-message";
    static readonly LastVisitedPageUrlKey = "last-visited-page-url";
    static readonly AdminModeEnabledKey = "admin-mode-enabled";
    static readonly TooltipKey = "tooltip";
    static readonly LastLoggedUserId = "last-logged-user-id";
    static readonly LastLoggedUserName = "last-logged-user-name";
    static readonly ImpersonationDataKey = "impersonation-data";
    static readonly LoggedOfflineKey = "logged-offline"; // Offline support
    static readonly NhsLoginKey = "nhs-login";
    static readonly NhsNonceKey = "nhs-nonce";
    static readonly LoginInProcess = "login-in-process"; // Help to differentiate a login from a refresh.
    static readonly IsRegisteredErrorMessage = "is-registered-error-message";
    static readonly PatientUpdatedMessage = "patient-updated-message";
    static readonly LoginRedirectParams = "login-redirect-params";
    static readonly appointmentsFilterKey = "appointment-filters";
    static readonly walkInSubEntityAdditionKey = "walk-in-sub-entity-addition";
    static readonly HomeScreenMessages = "home-screen-messages";
    
    /**
     * Uses a "localStorage" to store a data or delete item if "null" specified.
     * @param key The key of stored object.
     * @param item Stored value.
     * */
    static setItem(key: string, item: any): void {
        if (item == null) {
            localStorage.removeItem(`${AppStorage.ApplicationPrefix}.${key}`);
        } else {
            localStorage.setItem(`${AppStorage.ApplicationPrefix}.${key}`, JSON.stringify(item));
        }
    }

    /**
     * Uses a "sessionStorage" to store a data or delete item if "null" specified.
     * @param key The key of stored object.
     * @param item Stored value.
     * */
    static setItemForSession(key: string, item: any): void {
        if (item == null) {
            if (sessionStorage.getItem(key) !== null) {
                sessionStorage.removeItem(key);
            }
        } else {
            sessionStorage.setItem(key, JSON.stringify(item));
        }
    }

    static setOfflineEntity<T extends { id: string }>(url: string, entity: T): T {
        if (!entity) {
            localStorage.removeItem(`${AppStorage.ApplicationPrefix}.${this.correctUrl(url)}`);
        } else {
            const id = entity.id ?? UUID.UUID();
            entity.id = id;
            localStorage.setItem(`${AppStorage.ApplicationPrefix}.${this.correctUrl(url)}.${id}`, JSON.stringify(entity));
        }
        return entity;
    }

    /**
     * Uses a "localStorage" to get a stored data or "null" if nothing found or fetched data could not be parsed.
     * @param key The key of stored object.
     * */
    static getItem<T = string>(key: string): T {
        const storedString = localStorage.getItem(`${AppStorage.ApplicationPrefix}.${key}`);

        if (storedString == null) return null;

        try {
            return JSON.parse(storedString) as T;
        } catch (error) {
            return null;
        }
    }

    /**
     * Uses a "sessionStorage" to get a stored data or "null" if nothing found or fetched data could not be parsed.
     * @param key The key of stored object.
     * */
    static getItemFromSession<T>(key: string): T {
        const storedSessionString = sessionStorage.getItem(key);

        if (storedSessionString == null) return null;

        try {
            return JSON.parse(storedSessionString) as T;
        } catch (error) {
            return null;
        }
    }

    static popItemFromSession<T>(key: string): T {
        const storedSessionString = sessionStorage.getItem(key);
        sessionStorage.removeItem(key);

        if (storedSessionString == null) return null;

        try {
            return JSON.parse(storedSessionString) as T;
        } catch (error) {
            return null;
        }
    }

    static getOfflineEntity<T = string>(id: string, url: string): T {
        const storedString = localStorage.getItem(`${AppStorage.ApplicationPrefix}.${this.correctUrl(url)}.${id}`);

        if (storedString == null) return null;

        try {
            return JSON.parse(storedString) as T;
        } catch (error) {
            return null;
        }
    }

    static getOfflineEntities<T>(url: string): T[] {
        const urlModified: string = this.correctUrl(url);
        const keys: number = localStorage.length;
        const entities: T[] = [];

        for (let i = 0; i < keys; i++) {
            const key: string = localStorage.key(i);
            if (key.includes(urlModified)) {
                const storedString = localStorage.getItem(key);

                if (storedString == null) continue;

                try {
                    entities.push(JSON.parse(storedString) as T);
                } catch (error) {}
            }
        }

        return entities;
    }

    static extractOfflineEntity<T>(id: string, url: string): T {
        const entity: T = this.getOfflineEntity(id, url);
        if (entity == null) {
            return entity;
        }

        this.setOfflineEntity(`${this.correctUrl(url)}.${id}`, null);
        return entity;
    }

    static replaceOfflineEntity<T extends { id: string }>(entity: T, url: string): T {
        if (!!entity) {
            const id: string = entity.id;
            this.extractOfflineEntity<T>(id, url);
            this.setOfflineEntity<T>(url, entity);
        }
        return entity;
    }

    static correctUrl(url: string) {
        return url.replace(/\//g, ".");
    }
}
